import { useEffect, useState } from "react";
import { Plus, Trash2, Edit3, Search, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { category_id: "", topic_id: "", block_id: "", question: "", options: ["", "", "", ""], correct_index: 0, explanation: "" };

export default function AdminQuestions() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("__all__");
  const [filterTopic, setFilterTopic] = useState("__all__");
  const [skip, setSkip] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const limit = 20;

  const load = async () => {
    const params = new URLSearchParams();
    if (filterCat && filterCat !== "__all__") params.set("category_id", filterCat);
    if (filterTopic && filterTopic !== "__all__") params.set("topic_id", filterTopic);
    if (search) params.set("q", search);
    params.set("skip", String(skip));
    params.set("limit", String(limit));
    const { data } = await api.get(`/admin/questions?${params}`);
    setItems(data.items);
    setTotal(data.total);
  };

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, filterTopic, skip]);

  const currentCat = cats.find((c) => c.id === (draft.category_id || filterCat));
  const filterCatObj = cats.find((c) => c.id === filterCat);

  const openCreate = () => {
    setDraft({ ...EMPTY, category_id: filterCat !== "__all__" ? filterCat : (cats[0]?.id || "") });
    setEditOpen(true);
  };
  const openEdit = (q) => {
    setDraft({ ...q, options: q.options.length >= 4 ? q.options : [...q.options, ...Array(4 - q.options.length).fill("")] });
    setEditOpen(true);
  };

  const save = async () => {
    try {
      const opts = draft.options.filter((o) => o.trim());
      if (!draft.question.trim() || opts.length < 2) {
        toast.error("Pregunta y al menos 2 opciones son obligatorias");
        return;
      }
      const payload = {
        category_id: draft.category_id,
        topic_id: draft.topic_id || null,
        block_id: draft.block_id || null,
        question: draft.question.trim(),
        options: opts,
        correct_index: Math.min(draft.correct_index, opts.length - 1),
        explanation: draft.explanation || "",
      };
      if (draft.id) {
        await api.put(`/admin/questions/${draft.id}`, payload);
        toast.success("Pregunta actualizada");
      } else {
        await api.post("/admin/questions", payload);
        toast.success("Pregunta creada");
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/questions/${confirmDelete}`);
      toast.success("Pregunta eliminada");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gc-gold mb-2">Preguntas</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Banco de preguntas</h1>
          <p className="mt-1 text-sm text-slate-600">{total} preguntas en total</p>
        </div>
        <Button onClick={openCreate} className="bg-gc-gold hover:bg-gc-gold-light text-white" data-testid="create-question-button">
          <Plus className="h-4 w-4 mr-2" /> Nueva pregunta
        </Button>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Buscar</Label>
          <div className="relative mt-1.5">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (setSkip(0), load())}
              placeholder="Buscar en preguntas..."
              className="pl-9 h-10"
              data-testid="search-input"
            />
          </div>
        </div>
        <div className="w-44">
          <Label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Categoría</Label>
          <Select value={filterCat} onValueChange={(v) => { setFilterCat(v); setFilterTopic("__all__"); setSkip(0); }}>
            <SelectTrigger className="h-10 mt-1.5" data-testid="filter-category"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Tema</Label>
          <Select value={filterTopic} onValueChange={(v) => { setFilterTopic(v); setSkip(0); }} disabled={!filterCatObj}>
            <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {(filterCatObj?.topics || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <div className="mt-6 space-y-2" data-testid="questions-list">
        {items.length === 0 && <div className="p-8 text-center text-sm text-slate-500 border border-dashed border-slate-300 rounded-sm">No hay preguntas. ¡Crea la primera!</div>}
        {items.map((q) => (
          <div key={q.id} className="bg-white border border-slate-200 rounded-sm p-4 flex items-start gap-3 hover:border-slate-300 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-wider font-semibold">
                <span className="bg-gc-green/10 text-gc-green px-1.5 py-0.5 rounded-sm">{q.category_id}</span>
                {q.topic_id && <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{q.topic_id}</span>}
                {q.block_id && <span className="bg-gc-gold/10 text-gc-gold-dark px-1.5 py-0.5 rounded-sm">{q.block_id}</span>}
              </div>
              <div className="text-sm font-medium text-slate-900 line-clamp-2">{q.question}</div>
              <div className="mt-1.5 text-xs text-slate-500 line-clamp-1">
                Correcta: <strong className="text-emerald-700">{String.fromCharCode(65 + q.correct_index)}</strong> · {q.options[q.correct_index]}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => openEdit(q)} data-testid={`edit-${q.id}`}>
                <Edit3 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(q.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-${q.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between">
        <div className="text-xs text-slate-500">Mostrando {items.length ? skip + 1 : 0}–{skip + items.length} de {total}</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="border-slate-300">Anterior</Button>
          <Button variant="outline" size="sm" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} className="border-slate-300">Siguiente</Button>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{draft.id ? "Editar pregunta" : "Nueva pregunta"}</DialogTitle>
            <DialogDescription>Define la pregunta, las 4 opciones y la respuesta correcta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Categoría</Label>
                <Select value={draft.category_id} onValueChange={(v) => setDraft({ ...draft, category_id: v, topic_id: "", block_id: "" })}>
                  <SelectTrigger className="h-10 mt-1" data-testid="draft-category"><SelectValue placeholder="Categoría" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tema</Label>
                <Select value={draft.topic_id || "__none__"} onValueChange={(v) => setDraft({ ...draft, topic_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="(opcional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(sin tema)</SelectItem>
                    {(currentCat?.topics || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Bloque</Label>
                <Select value={draft.block_id || "__none__"} onValueChange={(v) => setDraft({ ...draft, block_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="(opcional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(sin bloque)</SelectItem>
                    {(currentCat?.blocks || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Enunciado</Label>
              <Textarea
                value={draft.question}
                onChange={(e) => setDraft({ ...draft, question: e.target.value })}
                rows={3}
                className="mt-1"
                data-testid="draft-question"
              />
            </div>

            <div>
              <Label className="text-xs">Opciones (marca la correcta)</Label>
              <div className="space-y-2 mt-1">
                {draft.options.map((o, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, correct_index: idx })}
                      className={`h-9 w-9 rounded-sm border flex-shrink-0 font-mono text-sm font-bold transition-colors ${
                        draft.correct_index === idx ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}
                      data-testid={`draft-correct-${idx}`}
                    >
                      {String.fromCharCode(65 + idx)}
                    </button>
                    <Input
                      value={o}
                      onChange={(e) => {
                        const ops = [...draft.options];
                        ops[idx] = e.target.value;
                        setDraft({ ...draft, options: ops });
                      }}
                      placeholder={`Opción ${String.fromCharCode(65 + idx)}`}
                      className="h-9"
                      data-testid={`draft-option-${idx}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Explicación (opcional)</Label>
              <Textarea
                value={draft.explanation}
                onChange={(e) => setDraft({ ...draft, explanation: e.target.value })}
                rows={2}
                className="mt-1"
                data-testid="draft-explanation"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-slate-300">Cancelar</Button>
            <Button onClick={save} className="bg-gc-gold hover:bg-gc-gold-light text-white" data-testid="save-question">
              {draft.id ? "Guardar cambios" : "Crear pregunta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pregunta?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
